import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "./NavBar";
import { markMessagesAsSeen } from "./useMessageCount";

const API_BASE_URL = "https://api.itrustsecure.com";
function getToken() { return localStorage.getItem("itrust_token"); }
function authHeaders() { return { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` }; }

export default function MessagesPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/conversations`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok) setConversations(Array.isArray(data) ? data : []);
    } catch (err) { console.warn(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); markMessagesAsSeen(); }, [load]);

  const s = {
    root: { fontFamily: "'Segoe UI', Arial, sans-serif", background: "transparent", color: "#0f172a", minHeight: "100vh" },
    main: { maxWidth: 700, margin: "0 auto", padding: "32px 20px" },
    title: { fontSize: 28, fontWeight: 800, marginBottom: 6, color: "#0f172a" },
    subtitle: { fontSize: 14, color: "#64748b", marginBottom: 28 },
    card: (unread) => ({ background: unread ? "rgba(59,130,246,0.08)" : "rgba(255,255,255,0.75)", border: `1px solid ${unread ? "rgba(59,130,246,0.3)" : "rgba(59,130,246,0.12)"}`, borderRadius: 16, padding: 16, marginBottom: 10, cursor: "pointer", display: "flex", gap: 12, alignItems: "center", backdropFilter: "blur(8px)", boxShadow: "0 2px 8px rgba(59,130,246,0.06)" }),
    listingThumb: { width: 52, height: 52, borderRadius: 10, objectFit: "cover", flexShrink: 0, border: "1px solid rgba(59,130,246,0.15)" },
    listingThumbPlaceholder: { width: 52, height: 52, borderRadius: 10, flexShrink: 0, background: "linear-gradient(135deg, rgba(59,130,246,0.1), rgba(124,58,237,0.1))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, border: "1px solid rgba(59,130,246,0.1)" },
    avatar: { width: 42, height: 42, borderRadius: "50%", background: "rgba(59,130,246,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 800, color: "#3b82f6", flexShrink: 0 },
    cardBody: { flex: 1, minWidth: 0 },
    cardName: { fontSize: 15, fontWeight: 700, marginBottom: 2, color: "#1e293b" },
    cardListing: { fontSize: 12, color: "#64748b", marginBottom: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    cardLastMsg: { fontSize: 12, color: "#94a3b8", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    cardMeta: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 },
    cardTime: { fontSize: 11, color: "#94a3b8" },
    pendingBadge: { background: "#d97706", color: "#fff", borderRadius: 999, padding: "2px 8px", fontSize: 10, fontWeight: 700 },
    empty: { textAlign: "center", padding: "60px 20px", color: "#94a3b8" },
  };

  return (
    <div style={s.root}>
      <NavBar user={user} onLogout={onLogout} />
      <div style={s.main}>
        <div style={s.title}>Messages</div>
        <div style={s.subtitle}>{conversations.length} conversation{conversations.length !== 1 ? "s" : ""}</div>
        {loading ? (
          <div style={s.empty}>Loading...</div>
        ) : conversations.length === 0 ? (
          <div style={s.empty}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>💬</div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: "#475569" }}>No messages yet</div>
            <div>Start a conversation from any listing page</div>
          </div>
        ) : conversations.map(convo => {
          const other = convo.buyerId === user?.id ? convo.seller : convo.buyer;
          const lastMsg = convo.messages?.[0];
          const hasPending = convo.priceProposals?.length > 0;
          const listingPhoto = convo.listing?.items?.[0]?.photoUrls?.[0];
          return (
            <div key={convo.id} style={s.card(hasPending)} onClick={() => navigate(`/messages/${convo.id}`)}>
              {listingPhoto ? (
                <img src={listingPhoto} alt="listing" style={s.listingThumb} onError={e => e.target.style.display = "none"} />
              ) : (
                <div style={s.listingThumbPlaceholder}>🛍️</div>
              )}
              <div style={s.avatar}>{other?.fullName?.charAt(0) || "?"}</div>
              <div style={s.cardBody}>
                <div style={s.cardName}>{other?.fullName}</div>
                {convo.listing && <div style={s.cardListing}>📦 {convo.listing.title}</div>}
                <div style={s.cardLastMsg}>{lastMsg ? lastMsg.body : "No messages yet"}</div>
              </div>
              <div style={s.cardMeta}>
                <div style={s.cardTime}>{lastMsg ? new Date(lastMsg.createdAt).toLocaleDateString() : ""}</div>
                {hasPending && <div style={s.pendingBadge}>💰 Offer</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}